/**
 * Navigation Board Types and Storage
 * 
 * Handles customization of Home and Entertainment screen items
 */

import { LucideIcon } from "lucide-react";
import {
  MessageSquare,
  Keyboard,
  Moon,
  Music,
  HeartHandshake,
  Youtube,
  Tv,
  Radio,
  Puzzle,
} from "lucide-react";

export interface NavigationItem {
  id: string;
  key: string;
  // Position key in fixed-slot layouts (e.g. the cross/flower home layout).
  // Defaults to `key`. Two items can swap positions by swapping their slotKey.
  // Used for cross-layout positioning so users can drag any item onto any
  // slot — including currently hidden ones — without changing the item's
  // identity (key, action) or its row order.
  slotKey?: string;
  labelHe: string;
  labelEn: string;
  labelRu?: string;
  labelAr?: string;
  iconName: string;
  action: string;
  color: string;
  order: number;
  visible: boolean;
}

// Helper: resolve which slot a navigation item occupies in fixed-slot layouts.
export function getSlotKey(item: NavigationItem): string {
  return item.slotKey ?? item.key;
}

export interface NavigationBoardData {
  id: "home" | "entertainment";
  items: NavigationItem[];
  updatedAt: string;
}

// Default Home items
// Default row order (RTL right-to-left): Rest(0), Help(1), Communication(2), Keyboard(3), Entertainment(4)
// Cross layout positions are derived by item key (Help=center, Speak=top, Media=bottom,
// Rest+Keyboard on the sides), independent of the linear order.
const DEFAULT_HOME_ITEMS: NavigationItem[] = [
  { id: "rest", key: "rest", labelHe: "מנוחה", labelEn: "Rest", labelRu: "Отдых", labelAr: "راحة", iconName: "Moon", action: "/rest", color: "teal", order: 0, visible: true },
  { id: "help", key: "help", labelHe: "עזרה", labelEn: "Help", labelRu: "Помощь", labelAr: "مساعدة", iconName: "HeartHandshake", action: "help", color: "help", order: 1, visible: true },
  { id: "communication", key: "communication", labelHe: "לוח תקשורת", labelEn: "Speak", labelRu: "Говорить", labelAr: "لوحة التواصل", iconName: "MessageSquare", action: "/communication", color: "green", order: 2, visible: true },
  { id: "keyboard", key: "keyboard", labelHe: "מקלדת", labelEn: "Keyboard", labelRu: "Быстрый набор", labelAr: "لوحة مفاتيح", iconName: "Keyboard", action: "/aac-keyboard", color: "pink", order: 3, visible: true },
  { id: "entertainment", key: "entertainment", labelHe: "בידור", labelEn: "Media", labelRu: "Медиа", labelAr: "وسائط", iconName: "Music", action: "/entertainment", color: "purple", order: 4, visible: true },
];

// Default Entertainment items
// Row order: YouTube(0), Radio(1)
const DEFAULT_ENTERTAINMENT_ITEMS: NavigationItem[] = [
  { id: "youtube", key: "youtube", labelHe: "יוטיוב", labelEn: "YouTube", labelRu: "YouTube", labelAr: "يوتيوب", iconName: "Youtube", action: "/youtube", color: "help", order: 0, visible: true },
  { id: "radio", key: "radio", labelHe: "רדיו", labelEn: "Radio", labelRu: "Радио", labelAr: "راديو", iconName: "Radio", action: "/radio", color: "purple", order: 1, visible: true },
];

const STORAGE_KEY = "navigationBoards_v21"; // v21: rename EN "Communication Board" → "Speak" on home board
const OLD_STORAGE_KEYS = ["navigationBoards_v20", "navigationBoards_v19", "navigationBoards_v18"];

// Valid color categories
const VALID_COLORS = ["communication", "media", "utility", "help", "orange", "blue", "green", "purple", "pink", "yellow", "indigo", "teal"];

/**
 * Legacy labels that should be auto-replaced with the current default when found in
 * a user's saved board. If a saved label is in this list, it's a stale system default
 * (the user never edited it) and we refresh it. Any other value is treated as a user
 * edit and left untouched.
 *
 * IMPORTANT: When changing a default label in DEFAULT_HOME_ITEMS / DEFAULT_ENTERTAINMENT_ITEMS,
 * add the OLD value here under the matching item key + language, and bump STORAGE_KEY.
 * Otherwise existing users will keep seeing the old text until they manually reset.
 */
const LEGACY_LABELS: Record<string, { en?: string[]; he?: string[]; ru?: string[]; ar?: string[] }> = {
  // Home board
  communication: {
    en: ["Talker", "Communication", "Communication Board"],
    he: ["דיבור", "תקשורת"],
    ru: ["Общение", "Доска общения", "Коммуникация"],
    ar: ["تحدث", "تواصل"],
  },
  keyboard: {
    ru: ["Клавиатура"], // replaced by "Быстрый набор"
  },
  entertainment: {
    en: ["Entertainment"], // replaced by "Media"
    he: ["מדיה"],
  },
  // (Add future renames here)
};

// Migrate items: enrich with missing labelRu/labelAr from defaults AND refresh
// any label that still matches a known legacy system default.
function migrateNavItems(items: NavigationItem[], defaults: NavigationItem[]): NavigationItem[] {
  return items.map(item => {
    const def = defaults.find(d => d.key === item.key);
    if (!def) return item;

    const legacy = LEGACY_LABELS[item.key];
    const refresh = (current: string | undefined, defaultValue: string | undefined, legacyList: string[] | undefined): string | undefined => {
      if (!current) return defaultValue; // missing → fill from default
      if (legacyList && legacyList.includes(current)) return defaultValue; // stale system default → refresh
      return current; // user edit → keep
    };

    return {
      ...item,
      labelEn: refresh(item.labelEn, def.labelEn, legacy?.en) ?? item.labelEn,
      labelHe: refresh(item.labelHe, def.labelHe, legacy?.he) ?? item.labelHe,
      labelRu: refresh(item.labelRu, def.labelRu, legacy?.ru),
      labelAr: refresh(item.labelAr, def.labelAr, legacy?.ar),
    };
  });
}

// Valid action paths for entertainment items
const VALID_ENTERTAINMENT_ACTIONS: Record<string, string> = {
  youtube: "/youtube",
  radio: "/radio",
};

// Load board from localStorage
export function loadNavigationBoard(boardId: "home" | "entertainment"): NavigationBoardData {
  const defaults = boardId === "home" ? DEFAULT_HOME_ITEMS : DEFAULT_ENTERTAINMENT_ITEMS;
  
  // Try current version
  let raw = localStorage.getItem(STORAGE_KEY);
  
  // Migrate from any older version if needed.
  // For the home board, when migrating from v18 (pre-v19 order change), also re-apply
  // the new default `order` so the linear sequence matches the current default
  // (Rest, Help, Speak, Keyboard, Media) while preserving user customizations.
  let migratedFromV18OrEarlier = false;
  if (!raw) {
    for (const oldKey of OLD_STORAGE_KEYS) {
      const oldRaw = localStorage.getItem(oldKey);
      if (oldRaw) {
        localStorage.setItem(STORAGE_KEY, oldRaw);
        localStorage.removeItem(oldKey);
        raw = oldRaw;
        if (oldKey === "navigationBoards_v18") migratedFromV18OrEarlier = true;
        break;
      }
    }
  }
  
  try {
    if (raw) {
      const boards: NavigationBoardData[] = JSON.parse(raw);
      const board = boards.find(b => b.id === boardId);
      if (board) {
        // Refresh stale system labels (EN/HE/RU/AR), enrich missing translations,
        // and fix invalid colors/actions. Preserves any value the user actually edited.
        board.items = migrateNavItems(board.items, defaults).map(item => {
          let fixedItem = { ...item };
          const def = defaults.find(d => d.key === item.key);
          
          // Fix invalid colors
          if (!VALID_COLORS.includes(item.color)) {
            fixedItem.color = def?.color || "utility";
          }
          
          // Fix invalid actions for entertainment items
          if (boardId === "entertainment" && VALID_ENTERTAINMENT_ACTIONS[item.key]) {
            fixedItem.action = VALID_ENTERTAINMENT_ACTIONS[item.key];
          }
          
          return fixedItem;
        });

        // On migration from v18 of the home board, re-apply the new default order
        if (migratedFromV18OrEarlier && boardId === "home") {
          board.items = board.items.map(item => {
            const def = defaults.find(d => d.key === item.key);
            return def ? { ...item, order: def.order } : item;
          });
        }
        
        const allBoards: NavigationBoardData[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const idx = allBoards.findIndex(b => b.id === boardId);
        if (idx >= 0) allBoards[idx] = board;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allBoards));
        
        return board;
      }
    }
  } catch (e) {
    console.error("Failed to load navigation board:", e);
  }
  
  // Return default
  return {
    id: boardId,
    items: [...defaults],
    updatedAt: new Date().toISOString(),
  };
}

// Save board to localStorage
export function saveNavigationBoard(board: NavigationBoardData): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    let boards: NavigationBoardData[] = saved ? JSON.parse(saved) : [];
    
    const index = boards.findIndex(b => b.id === board.id);
    if (index >= 0) {
      boards[index] = board;
    } else {
      boards.push(board);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(boards));
    // Notify listeners (e.g., Home/Entertainment screens) that board data changed
    window.dispatchEvent(new CustomEvent('navigationBoardUpdated', { detail: { boardId: board.id } }));
  } catch (e) {
    console.error("Failed to save navigation board:", e);
  }
}

// Update a single item
export function updateNavigationItem(
  board: NavigationBoardData,
  itemId: string,
  updates: Partial<Omit<NavigationItem, 'id' | 'key' | 'action'>>
): NavigationBoardData {
  return {
    ...board,
    items: board.items.map(item =>
      item.id === itemId ? { ...item, ...updates } : { ...item }
    ),
    updatedAt: new Date().toISOString(),
  };
}

// Reorder items
export function reorderNavigationItems(
  board: NavigationBoardData,
  fromIndex: number,
  toIndex: number
): NavigationBoardData {
  // Important: the editor reorders based on the *display order* (order field),
  // not necessarily the underlying array order. Normalize first.
  const orderedItems = [...board.items]
    .sort((a, b) => a.order - b.order)
    .map(item => ({ ...item }));

  const [moved] = orderedItems.splice(fromIndex, 1);
  orderedItems.splice(toIndex, 0, moved);

  // Update order property on new objects
  const updatedItems = orderedItems.map((item, idx) => ({
    ...item,
    order: idx,
  }));
  
  return {
    ...board,
    items: updatedItems,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Swap two items between fixed slots (used by the cross/flower home layout).
 * The items keep their identity (key/action/labels/icon/color/visible); only
 * their `slotKey` (which slot they occupy in the cross) is exchanged.
 *
 * Works regardless of `visible` — a hidden slot is still a valid swap target,
 * which is what lets caregivers drag items onto currently hidden positions.
 */
export function swapNavigationSlots(
  board: NavigationBoardData,
  slotA: string,
  slotB: string
): NavigationBoardData {
  if (slotA === slotB) return board;

  const itemA = board.items.find(i => getSlotKey(i) === slotA);
  const itemB = board.items.find(i => getSlotKey(i) === slotB);
  if (!itemA || !itemB) return board;

  return {
    ...board,
    items: board.items.map(item => {
      if (item.id === itemA.id) return { ...item, slotKey: slotB };
      if (item.id === itemB.id) return { ...item, slotKey: slotA };
      return { ...item };
    }),
    updatedAt: new Date().toISOString(),
  };
}
export function resetNavigationBoard(boardId: "home" | "entertainment"): NavigationBoardData {
  const board: NavigationBoardData = {
    id: boardId,
    items: boardId === "home" ? [...DEFAULT_HOME_ITEMS] : [...DEFAULT_ENTERTAINMENT_ITEMS],
    updatedAt: new Date().toISOString(),
  };
  saveNavigationBoard(board);
  return board;
}

// Get default items for reference
export function getDefaultItems(boardId: "home" | "entertainment"): NavigationItem[] {
  return boardId === "home" ? DEFAULT_HOME_ITEMS : DEFAULT_ENTERTAINMENT_ITEMS;
}
